import React, { useState, useEffect, Fragment, useRef, useMemo } from "react";
import Multiselect from 'multiselect-react-dropdown';
import CsVersionUpdate from "../../models/CsVersionUpdate";
import { fetchSoftwareVersions } from '../../api/csDiagnosticsApi';
import { VersionCellDisplay } from './versionDisplayUtils';

function buildStationMaps(chargingStations) {
  const c2ci = {};
  const ci2z = {};
  const z2s = {};
  const s2p = {};
  const ci2c = {};
  const z2ci = {};
  const isUsableValue = value => typeof value === 'string' && value.trim() !== '';
  const uniqueOptions = key => Array.from(new Set(
    chargingStations.map(item => item[key]).filter(isUsableValue)
  )).map(value => ({ [key]: value }));

  chargingStations.forEach(item => {
    const { circle, city, charging_zone, device_id } = item;

    if (isUsableValue(circle) && isUsableValue(city)) {
      if (!c2ci[circle]) c2ci[circle] = new Set();
      c2ci[circle].add(city);
      ci2c[city] = circle;
    }

    if (isUsableValue(city) && isUsableValue(charging_zone)) {
      if (!ci2z[city]) ci2z[city] = new Set();
      ci2z[city].add(charging_zone);
      z2ci[charging_zone] = city;
    }

    if (isUsableValue(charging_zone) && isUsableValue(device_id)) {
      if (!z2s[charging_zone]) z2s[charging_zone] = new Set();
      z2s[charging_zone].add(device_id);
    }

    s2p[device_id] = { charging_zone, city, circle };
  });

  return {
    circleToCities: Object.fromEntries(Object.entries(c2ci).map(([k, v]) => [k, Array.from(v)])),
    cityToZones: Object.fromEntries(Object.entries(ci2z).map(([k, v]) => [k, Array.from(v)])),
    zoneToStations: Object.fromEntries(Object.entries(z2s).map(([k, v]) => [k, Array.from(v)])),
    stationToParents: s2p,
    cityToCircle: ci2c,
    zoneToCity: z2ci,
    uniqueCircles: uniqueOptions('circle'),
    uniqueCities: uniqueOptions('city'),
    uniqueZones: uniqueOptions('charging_zone'),
    uniqueStations: uniqueOptions('device_id')
  };
}

const FormPartial = (props) => {
  const { csVersionUpdate, setCsVersionUpdate, chargingStations, refreshChargingStations, onSubmitClick } = props
  let csVersionUpdateObj = new CsVersionUpdate(csVersionUpdate);
  const [softwareVersions, setSoftwareVersions] = useState([]);
  const circleRef = useRef();
  const cityRef = useRef();
  const chargingZoneRef = useRef();
  const deviceIDRef = useRef();
  const [tableData, setTableData] = useState([]);

  const maps = useMemo(() => buildStationMaps(chargingStations), [chargingStations]);

  const loadSoftwareVersions = async () => {
    try {
      const data = await fetchSoftwareVersions();
      if (data.length > 0) {
        setSoftwareVersions(data);
      }
    } catch (error) {
      console.error("Error fetching software versions:", error);
    }
  };

  useEffect(() => {
    loadSoftwareVersions();
  }, []);

  useEffect(() => {
    let csData = chargingStations.filter(item => csVersionUpdateObj['device_ids'].includes(item['device_id']));
    setTableData(csData.map(
      ({ circle, city, charging_zone, device_id, battery_swap_count_yesterday, current_android_version, android_version_last_updated_at, current_bin_version, bin_version_last_updated_at}) =>
      ({ circle, city, charging_zone, device_id, battery_swap_count_yesterday, current_android_version, android_version_last_updated_at, current_bin_version, bin_version_last_updated_at})));
  }, [csVersionUpdateObj.device_ids, chargingStations])

  const validateValue = (field) => {
    csVersionUpdateObj.validate(field);
    if (field === 'circle_ids' || field === 'city_ids' || field === 'charging_zone_ids'){
      csVersionUpdateObj.validate('device_ids');
    }
    if(field === 'version'){
      csVersionUpdateObj.validate('type');
    } else if (field === 'type'){
      csVersionUpdateObj.validate('version');
    }
  }

  const changeValue = (field, value) => {
    csVersionUpdateObj[field] = value;
    if (field === 'type') {
      csVersionUpdateObj.version = ""; // Clear version when type changes
    } else {
      validateValue(field);
    }
    setCsVersionUpdate(csVersionUpdateObj.toStateJson());
  }

  const onMultiSelect = async (key, selectedItem, ogKey) => {
    const [freshData] = await Promise.all([
      refreshChargingStations(),
      loadSoftwareVersions(),
    ]);
    const freshMaps = buildStationMaps(freshData);
    const value = selectedItem[ogKey];

    // Add directly selected item
    if (!csVersionUpdateObj[key].includes(value)) {
      csVersionUpdateObj[key].push(value);
    }

    // Bidirectional Logic: Handle Children and Parents
    if (key === 'circle_ids') {
      const cities = freshMaps.circleToCities[value] || [];
      cities.forEach(ci => {
        if (!csVersionUpdateObj.city_ids.includes(ci)) csVersionUpdateObj.city_ids.push(ci);
        const zones = freshMaps.cityToZones[ci] || [];
        zones.forEach(z => {
          if (!csVersionUpdateObj.charging_zone_ids.includes(z)) csVersionUpdateObj.charging_zone_ids.push(z);
          const stations = freshMaps.zoneToStations[z] || [];
          stations.forEach(s => {
            if (!csVersionUpdateObj.device_ids.includes(s)) csVersionUpdateObj.device_ids.push(s);
          });
        });
      });
    } else if (key === 'city_ids') {
      const circle = freshMaps.cityToCircle[value];
      if (circle && !csVersionUpdateObj.circle_ids.includes(circle)) csVersionUpdateObj.circle_ids.push(circle);
      const zones = freshMaps.cityToZones[value] || [];
      zones.forEach(z => {
        if (!csVersionUpdateObj.charging_zone_ids.includes(z)) csVersionUpdateObj.charging_zone_ids.push(z);
        const stations = freshMaps.zoneToStations[z] || [];
        stations.forEach(s => {
          if (!csVersionUpdateObj.device_ids.includes(s)) csVersionUpdateObj.device_ids.push(s);
        });
      });
    } else if (key === 'charging_zone_ids') {
      const city = freshMaps.zoneToCity[value];
      if (city) {
        if (!csVersionUpdateObj.city_ids.includes(city)) csVersionUpdateObj.city_ids.push(city);
        const circle = freshMaps.cityToCircle[city];
        if (circle && !csVersionUpdateObj.circle_ids.includes(circle)) csVersionUpdateObj.circle_ids.push(circle);
      }
      const stations = freshMaps.zoneToStations[value] || [];
      stations.forEach(s => {
        if (!csVersionUpdateObj.device_ids.includes(s)) csVersionUpdateObj.device_ids.push(s);
      });
    } else if (key === 'device_ids') {
      const p = freshMaps.stationToParents[value];
      if (p) {
        if (typeof p.charging_zone === 'string' && p.charging_zone.trim() && !csVersionUpdateObj.charging_zone_ids.includes(p.charging_zone)) csVersionUpdateObj.charging_zone_ids.push(p.charging_zone);
        if (typeof p.city === 'string' && p.city.trim() && !csVersionUpdateObj.city_ids.includes(p.city)) csVersionUpdateObj.city_ids.push(p.city);
        if (typeof p.circle === 'string' && p.circle.trim() && !csVersionUpdateObj.circle_ids.includes(p.circle)) csVersionUpdateObj.circle_ids.push(p.circle);
      }
    }

    validateValue(key);
    setCsVersionUpdate(csVersionUpdateObj.toStateJson());
  }

  const onMultiRemove = async (key, removedItem, ogKey) => {
    const [freshData] = await Promise.all([
      refreshChargingStations(),
      loadSoftwareVersions(),
    ]);
    const freshMaps = buildStationMaps(freshData);
    const value = removedItem[ogKey];

    // Remove the item itself
    csVersionUpdateObj[key] = csVersionUpdateObj[key].filter(id => id !== value);

    // Cascading Deselection: Remove Children Only
    if (key === 'circle_ids') {
      const cities = freshMaps.circleToCities[value] || [];
      cities.forEach(ci => {
        csVersionUpdateObj.city_ids = csVersionUpdateObj.city_ids.filter(id => id !== ci);
        const zones = freshMaps.cityToZones[ci] || [];
        zones.forEach(z => {
          csVersionUpdateObj.charging_zone_ids = csVersionUpdateObj.charging_zone_ids.filter(id => id !== z);
          const stations = freshMaps.zoneToStations[z] || [];
          stations.forEach(s => {
            csVersionUpdateObj.device_ids = csVersionUpdateObj.device_ids.filter(id => id !== s);
          });
        });
      });
    } else if (key === 'city_ids') {
      const zones = freshMaps.cityToZones[value] || [];
      zones.forEach(z => {
        csVersionUpdateObj.charging_zone_ids = csVersionUpdateObj.charging_zone_ids.filter(id => id !== z);
        const stations = freshMaps.zoneToStations[z] || [];
        stations.forEach(s => {
          csVersionUpdateObj.device_ids = csVersionUpdateObj.device_ids.filter(id => id !== s);
        });
      });
    } else if (key === 'charging_zone_ids') {
      const stations = freshMaps.zoneToStations[value] || [];
      stations.forEach(s => {
        csVersionUpdateObj.device_ids = csVersionUpdateObj.device_ids.filter(id => id !== s);
      });
    }

    validateValue(key);
    setCsVersionUpdate(csVersionUpdateObj.toStateJson());
  }

  const beautifyString = (hash) => {
    return hash.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  }

  const filteredVersionOptions = () => {
    if (!csVersionUpdateObj.type) return [];
    const targetType = csVersionUpdateObj.type === 'android' ? 'apk' : 'bin';
    return softwareVersions.filter(v => v.type === targetType);
  }

  return <Fragment>
    <div className="col-12">
      <div className="cs-vup-section cs-vup-section--filters">
        <div className="cs-vup-section__title-bar cs-vup-section__title-bar--subtle">
          Filter Charging Stations
        </div>
        <div className="cs-vup-section__body">
          <div className="row g-3">
            <div className="col-md-6">
              <label htmlFor='circle_ids' className="form-label">Circle</label>
              <Multiselect
                id='circle_ids'
                options={maps.uniqueCircles}
                selectedValues={maps.uniqueCircles.filter(i => csVersionUpdateObj.circle_ids.includes(i.circle))}
                displayValue={'circle'}
                avoidHighlightFirstOption={true}
                onSelect={(selectedList, selectedItem) => onMultiSelect("circle_ids", selectedItem, 'circle')}
                onRemove={(selectedList, removedItem) => onMultiRemove("circle_ids", removedItem, 'circle')}
                customArrow={true}
                placeholder='Select Circle'
                ref={circleRef}
              />
            </div>
            <div className="col-md-6">
              <label htmlFor='city_ids' className="form-label">City</label>
              <Multiselect
                id='city_ids'
                options={maps.uniqueCities}
                selectedValues={maps.uniqueCities.filter(i => csVersionUpdateObj.city_ids.includes(i.city))}
                displayValue={'city'}
                avoidHighlightFirstOption={true}
                onSelect={(selectedList, selectedItem) => onMultiSelect("city_ids", selectedItem, 'city')}
                onRemove={(selectedList, removedItem) => onMultiRemove("city_ids", removedItem, 'city')}
                customArrow={true}
                placeholder='Select City'
                ref={cityRef}
              />
            </div>
            <div className="col-md-6">
              <label htmlFor='charging_zone_ids' className="form-label">Charging Zone</label>
              <Multiselect
                id='charging_zone_ids'
                options={maps.uniqueZones}
                selectedValues={maps.uniqueZones.filter(i => csVersionUpdateObj.charging_zone_ids.includes(i.charging_zone))}
                displayValue={'charging_zone'}
                avoidHighlightFirstOption={true}
                onSelect={(selectedList, selectedItem) => onMultiSelect("charging_zone_ids", selectedItem, 'charging_zone')}
                onRemove={(selectedList, removedItem) => onMultiRemove("charging_zone_ids", removedItem, 'charging_zone')}
                customArrow={true}
                placeholder='Select Charging Zone'
                ref={chargingZoneRef}
              />
            </div>
            <div className="col-md-6">
              <label htmlFor='device_ids' className="form-label">Charging Station</label>
              <Multiselect
                id='device_ids'
                options={maps.uniqueStations}
                selectedValues={maps.uniqueStations.filter(i => csVersionUpdateObj.device_ids.includes(i.device_id))}
                displayValue={'device_id'}
                avoidHighlightFirstOption={true}
                onSelect={(selectedList, selectedItem) => onMultiSelect("device_ids", selectedItem, 'device_id')}
                onRemove={(selectedList, removedItem) => onMultiRemove("device_ids", removedItem, 'device_id')}
                customArrow={true}
                placeholder='Select Charging Station'
                ref={deviceIDRef}
              />
              <div id="device_ids_help" className="form-text text-danger mt-0">
                {csVersionUpdateObj.errors.device_ids}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    {
      tableData.length > 0 &&
      <div className="col-12">
        <div className="cs-vup-section">
          <div className="cs-vup-section__title-bar">Selected Charging Stations</div>
          <div className="cs-vup-section__body cs-vup-section__body--flush">
            <div className="cs-vup-table-container custom-scrollbar">
              <table className="table cs-vup-table">
                <thead>
                  <tr>
                    {tableData[0] && Object.keys(tableData[0]).map((heading) => {
                      return <th key={`simple_heading${heading}`} scope="col">
                        {beautifyString(heading)}
                      </th>
                    })}
                  </tr>
                </thead>
                <tbody>
                  {tableData && tableData.map((obj, index) => {
                    return <tr key={`simple_data${index}`}>
                      {Object.keys(obj).map((key) => {
                        return (
                          <td key={`simple_td_${key}`}>
                            <VersionCellDisplay fieldKey={key} value={obj[key]} />
                          </td>
                        );
                      })}
                    </tr>
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    }
    <div className="col-12">
      <div className="cs-vup-section">
        <div className="cs-vup-section__title-bar">Version Selection</div>
        <div className="cs-vup-section__body">
          <div className="row g-3 align-items-start">
            <div className="col-md-3">
              <label htmlFor="type" className="form-label">Version Type</label>
              <div className="cs-vup-type-container">
                {csVersionUpdateObj.version_type && Object.keys(csVersionUpdateObj.version_type).map((type) => (
                  <div className="type-option" key={type}>
                    <input
                      id={type}
                      checked={csVersionUpdateObj.type === type}
                      type="radio"
                      onChange={(e) => changeValue('type', e.target.id)}
                      name="type"
                    />
                    <label htmlFor={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </label>
                  </div>
                ))}
              </div>
              <div className="cs-vup-error-container">
                {csVersionUpdateObj.errors.type && (
                  <div id="type_help" className="form-text text-danger mt-1">
                    {csVersionUpdateObj.errors.type}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-4">
              <label htmlFor="version" className="form-label">Version</label>
              <Multiselect
                id="version"
                options={filteredVersionOptions()}
                selectedValues={filteredVersionOptions().filter(v => v.fileName === csVersionUpdateObj.version)}
                onSelect={(selectedList, selectedItem) => changeValue('version', selectedItem.fileName)}
                onRemove={() => changeValue('version', '')}
                displayValue="fileName"
                selectionLimit={1}
                avoidHighlightFirstOption={true}
                placeholder="Select Version"
                customArrow={true}
                className="font-size-12"
            style={{
              multiselectContainer: { minHeight: '35px' },
              searchWrapper: { minHeight: '35px', padding: '4px 8px' }
            }}
              />
              <div className="cs-vup-error-container">
                {csVersionUpdateObj.errors.version && (
                  <div id="version_help" className="form-text text-danger mt-1">
                    {csVersionUpdateObj.errors.version}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-5">
              <label htmlFor="remarks" className="form-label">Remark (Reason for update)</label>
              <textarea
                id="remarks"
                value={csVersionUpdateObj.remarks}
                onChange={(e) => changeValue('remarks', e.target.value)}
                className="form-control"
                rows="1"
                style={{ height: '35px', minHeight: '35px' }}
                placeholder='Enter reason...'
              />
              <div className="cs-vup-error-container">
                {csVersionUpdateObj.errors.remarks && (
                  <div id="remarks_help" className="form-text text-danger mt-1">
                    {csVersionUpdateObj.errors.remarks}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="cs-vup-submit-row">
            <button
              type="button"
              className="btn btn-voltup-theme cs-vup-submit-btn"
              onClick={onSubmitClick}
            >
              SUBMIT
            </button>
          </div>
        </div>
      </div>
    </div>
  </Fragment>
}

export default FormPartial;
